import React, { useState, useEffect, useCallback } from "react";

/* ──────────────────────────────────────────────
   10 slides — image : /static/help/slide-N.png
   Pour ajouter vos captures : placez les fichiers
   dans  backend/static/help/slide-1.png  …  slide-10.png
─────────────────────────────────────────────── */
const SLIDES = [
  {
    num: 1,
    icon: "bi-mortarboard-fill",
    accent: "#6366f1",
    title: "Bienvenue sur Quick Learning !",
    emoji: "🎓",
    desc: "Votre plateforme de soutien scolaire au Cameroun. Accédez à vos cours, QCM, vidéos pédagogiques, exercices notés et formules utiles — le tout organisé selon votre manuel scolaire. Faites défiler ces slides pour découvrir toutes les fonctionnalités disponibles.",
  },
  {
    num: 2,
    icon: "bi-key-fill",
    accent: "#f59e0b",
    title: "Connexion & Code manuel",
    emoji: "🔑",
    desc: "Pour vous connecter, saisissez votre nom d'utilisateur et votre mot de passe. Si vous êtes élève, ajoutez le code manuel trouvé à l'intérieur de votre livre papier (format : QL-PHY-TLE-1-XXX). Astuce : les espaces se convertissent automatiquement en tirets pendant la saisie.",
  },
  {
    num: 3,
    icon: "bi-grid-fill",
    accent: "#0ea5e9",
    title: "Votre tableau de bord",
    emoji: "🏠",
    desc: "Une fois connecté, votre tableau de bord affiche un résumé de votre progression. Le menu latéral à gauche vous donne accès à toutes les sections : Leçons, QCM, Vidéos, Exercices, Devoirs, Formules utiles, Messagerie, Résultats et bien plus. Sur mobile, ouvrez-le avec le bouton ☰ en haut.",
  },
  {
    num: 4,
    icon: "bi-book-fill",
    accent: "#10b981",
    title: "Leçons interactives",
    emoji: "📖",
    desc: "Vos leçons sont organisées par module selon votre manuel. Cliquez sur une leçon pour la lire, visualiser les schémas et prendre des notes. La plateforme retient automatiquement votre progression — une coche verte indique les leçons déjà consultées.",
  },
  {
    num: 5,
    icon: "bi-patch-question-fill",
    accent: "#8b5cf6",
    title: "QCM — Questions à choix multiples",
    emoji: "✅",
    desc: "Chaque module propose des QCM (Questions à Choix Multiples) chronométrés pour tester vos connaissances. Répondez aux questions, soumettez vos réponses et consultez immédiatement votre score ainsi que les corrections détaillées pour comprendre vos erreurs.",
  },
  {
    num: 6,
    icon: "bi-play-circle-fill",
    accent: "#ef4444",
    title: "Vidéos pédagogiques",
    emoji: "🎬",
    desc: "Des vidéos pédagogiques accompagnent vos leçons pour mieux comprendre les concepts difficiles. Les vidéos se lancent directement depuis la plateforme avec le lecteur intégré. Votre historique de visionnage est sauvegardé pour que vous puissiez reprendre là où vous avez arrêté.",
  },
  {
    num: 7,
    icon: "bi-pencil-square",
    accent: "#f97316",
    title: "Exercices & Devoirs",
    emoji: "📝",
    desc: "Les exercices interactifs vous permettent de pratiquer et d'auto-évaluer vos acquis. Si vous bloquez sur une question, vous pouvez demander l'aide d'un enseignant directement depuis l'exercice. Vos soumissions sont enregistrées et vos résultats consultables à tout moment.",
  },
  {
    num: 8,
    icon: "bi-infinity",
    accent: "#0ea5e9",
    title: "Formules utiles",
    emoji: "∞",
    desc: "La section Formules utiles regroupe toutes les formules mathématiques et scientifiques importantes, classées par module. Chaque formule est affichée en notation mathématique lisible. Les images associées peuvent être agrandies en plein écran d'un simple clic pour mieux visualiser les schémas.",
  },
  {
    num: 9,
    icon: "bi-chat-dots-fill",
    accent: "#6366f1",
    title: "Messagerie avec vos enseignants",
    emoji: "💬",
    desc: "La messagerie intégrée vous permet de communiquer directement avec vos enseignants et vos camarades. Posez vos questions, partagez vos difficultés et recevez des réponses personnalisées. La messagerie prend en charge les formules mathématiques pour faciliter les explications scientifiques.",
  },
  {
    num: 10,
    icon: "bi-bell-fill",
    accent: "#f59e0b",
    title: "Notifications & Calculatrice",
    emoji: "🔔",
    desc: "Soyez informé en temps réel des nouvelles formules, corrections de devoirs et messages de vos enseignants. La cloche en haut à droite affiche le nombre de notifications non lues. La calculatrice scientifique flottante est disponible à tout moment depuis la barre supérieure.",
  },
  {
    num: 11,
    icon: "bi-journal-check",
    accent: "#dc2626",
    title: "Devoirs notés avec délai",
    emoji: "📋",
    desc: "Vos enseignants publient des devoirs avec une date limite de soumission. Vous devez rédiger votre réponse et la soumettre avant l'heure fixée. Une fois le délai écoulé, l'enseignant corrige votre travail, laisse un commentaire et vous attribue une note visible directement sur la plateforme.",
  },
  {
    num: 12,
    icon: "bi-search",
    accent: "#0891b2",
    title: "Recherche par code de contenu",
    emoji: "🔍",
    desc: "Chaque leçon, exercice ou QCM de votre manuel papier possède un code unique. Saisissez ce code dans la barre de recherche de la plateforme pour accéder instantanément au contenu correspondant — plus besoin de parcourir toutes les sections pour retrouver un exercice précis.",
  },
  {
    num: 13,
    icon: "bi-bar-chart-fill",
    accent: "#7c3aed",
    title: "Suivi de vos notes & activités",
    emoji: "📊",
    desc: "La section Résultats & Activités vous donne une vue complète de vos performances : notes obtenues aux QCM, exercices et devoirs, temps passé sur la plateforme, leçons consultées et progression par module. Utilisez ces données pour identifier vos points forts et les chapitres à renforcer.",
  },
  {
    num: 14,
    icon: "bi-people-fill",
    accent: "#059669",
    title: "Groupes d'études entre élèves",
    emoji: "👥",
    desc: "Rejoignez ou découvrez des groupes d'études créés par vos enseignants. Au sein d'un groupe, les élèves d'une même classe peuvent échanger, poser des questions, partager des ressources et s'entraider mutuellement. C'est l'espace idéal pour réviser en équipe et progresser ensemble sans blocage.",
  },
];

function SlideImage({ num, accent, icon, title, emoji }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = `/static/help/slide-${num}.png`;

  return (
    <div className="ht-img-wrap">
      {!failed && (
        <img
          src={src}
          alt={title}
          className={`ht-img${loaded ? " ht-img--loaded" : ""}`}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
      {/* Fallback illustré si l'image n'existe pas encore */}
      {(failed || !loaded) && (
        <div
          className="ht-img-fallback"
          style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}11)`, borderColor: `${accent}33` }}
        >
          <div className="ht-fb-icon" style={{ color: accent }}>
            <i className={`bi ${icon}`}></i>
          </div>
          <div className="ht-fb-emoji">{emoji}</div>
          <div className="ht-fb-label" style={{ color: accent }}>{title}</div>
          <div className="ht-fb-hint">📸 Capture d'écran à venir</div>
        </div>
      )}
    </div>
  );
}

export default function HelpTour({ onClose }) {
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const total = SLIDES.length;

  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => {
    if (idx < total - 1) setIdx(i => i + 1);
    else onClose();
  }, [idx, total, onClose]);

  // Fermer avec ESC, naviguer avec flèches
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, next, prev]);

  // Bloquer le scroll du body
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const progress = ((idx + 1) / total) * 100;

  return (
    <div className="ht-overlay">
      <div className="ht-modal">

        {/* Barre de progression */}
        <div className="ht-progress-bar">
          <div className="ht-progress-fill" style={{ width: `${progress}%`, background: slide.accent }} />
        </div>

        {/* En-tête */}
        <div className="ht-header">
          <span className="ht-counter">{idx + 1} / {total}</span>
          <button className="ht-close" onClick={onClose} title="Ignorer la présentation">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Corps : image + texte */}
        <div className="ht-body">
          <SlideImage
            num={slide.num}
            accent={slide.accent}
            icon={slide.icon}
            title={slide.title}
            emoji={slide.emoji}
          />

          <div className="ht-text">
            <div className="ht-icon-circle" style={{ background: `${slide.accent}18`, color: slide.accent }}>
              <i className={`bi ${slide.icon}`}></i>
            </div>
            <h2 className="ht-title">{slide.title}</h2>
            <p className="ht-desc">{slide.desc}</p>
          </div>
        </div>

        {/* Pied : points + boutons */}
        <div className="ht-footer">
          <button className="ht-btn ht-btn-ghost" onClick={prev} disabled={idx === 0}>
            <i className="bi bi-arrow-left me-1"></i>Précédent
          </button>

          <div className="ht-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className={`ht-dot${i === idx ? " ht-dot--active" : ""}`}
                style={i === idx ? { background: slide.accent } : {}}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            className="ht-btn ht-btn-primary"
            style={{ background: slide.accent }}
            onClick={next}
          >
            {idx < total - 1 ? (
              <>Suivant<i className="bi bi-arrow-right ms-1"></i></>
            ) : (
              <>Commencer<i className="bi bi-check-lg ms-1"></i></>
            )}
          </button>
        </div>

        {/* Lien ignorer */}
        {idx < total - 1 && (
          <div className="ht-skip">
            <button className="ht-skip-btn" onClick={onClose}>
              Ignorer la présentation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
